import { area, booleanIntersects } from '@turf/turf';
import { getIn, useFormikContext } from 'formik';
import {
  useGetCitiesLazyQuery,
  useGetInterventionsFieldDepartmentsQuery,
} from 'generated/graphql';
import { Feature, FeatureCollection, Geometry, Polygon } from 'geojson';
import L, { LatLng, Layer, LeafletMouseEvent } from 'leaflet';
import {
  ProFormUpdateFields,
  ProPostalAddressField,
} from 'pages/pros/pro-form/pro-form-update-fields-type';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { ToastError } from 'utils';
import { toGeometryGeoJSON } from 'utils/geometry';
import { SelectionMode } from './interventions-field-toolbar';
import {
  DepartmentProperties,
  FeatureDepartment,
  FeatureIntervention,
  OtherProperties,
} from './interventions-field-repository';
import InterventionsFieldView from './interventions-field-view';

type InterventionCollectionAction =
  | {
      type: 'insert' | 'upsert' | 'update' | 'delete';
      intervention: Feature<Geometry>[];
    }
  | {
      type: 'selectDepartment';
      intervention: Feature<Geometry>[];
    }
  | {
      type: 'unselectDepartment';
      departmentId: number;
    };

const interventionCollectionReducer = (
  state: FeatureCollection<Geometry>,
  action: InterventionCollectionAction,
): FeatureCollection<Geometry> => {
  switch (action.type) {
    case 'insert':
      return {
        ...state,
        features: [
          ...state.features,
          ...action.intervention.filter((intervention) => {
            return !state.features.some((city) => {
              return city.properties!.id === intervention.properties!.id;
            });
          }),
        ],
      };
    case 'upsert':
      const citiesToInsert = action.intervention.filter(
        (newCity) =>
          !state.features.some(
            (city) => city.properties!.id === newCity.properties!.id,
          ),
      );

      const citiesToUpdate = action.intervention.filter((newCity) =>
        state.features.some(
          (city) =>
            city.properties!.id === newCity.properties!.id &&
            city.properties!.type !== newCity.properties!.type,
        ),
      );

      return {
        ...state,
        features: [
          ...state.features.filter(
            (city) =>
              !citiesToInsert.some(
                (newCity) => newCity.properties!.id === city.properties!.id,
              ),
          ),
          ...citiesToInsert,
        ].map(
          (city) =>
            citiesToUpdate.find(
              (newCity) => newCity.properties!.id === city.properties!.id,
            ) || city,
        ),
      };

    case 'update':
      const updatedCities = {
        ...state,
        // Replace cities with the updated ones
        features: state.features.map(
          (city) =>
            action.intervention.find(
              (c) => c.properties!.id === city.properties!.id,
            ) ?? city,
        ),
      };

      return updatedCities;

    case 'delete':
      return {
        ...state,
        // Delete cities from the state
        features: state.features.filter(
          (city) =>
            !action.intervention.find(
              (c) => c.properties!.id === city.properties!.id,
            ),
        ),
      };

    case 'selectDepartment':
      // Include the department cities, but keep the cities already included or
      // excluded as they are (an excluded city stays excluded)
      return {
        ...state,
        features: [
          ...state.features.map((city) =>
            city.properties?.type === 'touches' &&
            action.intervention.some(
              (newCity) => newCity.properties?.id === city.properties?.id,
            )
              ? { ...city, properties: { ...city.properties, type: 'include' } }
              : city,
          ),
          ...action.intervention.filter(
            (newCity) =>
              !state.features.some(
                (city) => city.properties?.id === newCity.properties?.id,
              ),
          ),
        ],
      };

    case 'unselectDepartment':
      return {
        ...state,
        features: state.features.filter(
          (city) => city.properties?.departmentId !== action.departmentId,
        ),
      };
  }
};

const createInitialState = (): FeatureCollection<Geometry> => ({
  type: 'FeatureCollection',
  features: [],
});

type InterventionsFieldContainerProps = {
  name: string;
  getCitiesQuery: ReturnType<typeof useGetCitiesLazyQuery>[0];
  proPostalAddress?: ProPostalAddressField;
  interventionTypes: string[];
  defaultMapCenter?: LatLng | null;
  otherFeatures?: FeatureCollection<Geometry, OtherProperties>;
  displayBilikZones?: boolean;
  label?: string;
};

const InterventionsFieldContainer: FunctionComponent<
  InterventionsFieldContainerProps
> = ({
  name,
  getCitiesQuery,
  defaultMapCenter,
  interventionTypes,
  proPostalAddress,
  otherFeatures,
  displayBilikZones,
  label,
}) => {
  const { initialValues, setFieldValue } = useFormikContext();

  const defaultValues = useMemo(
    () => getIn(initialValues, name),
    [initialValues, name],
  );

  const [loadingInterventions, setLoadingInterventions] = useState(
    !!defaultValues?.length,
  );

  const [selectionMode, setSelectionMode] = useState<SelectionMode>(
    defaultValues.length ? 'click' : 'lasso',
  );

  const [interventions, setInterventions] = useReducer(
    interventionCollectionReducer,
    undefined,
    createInitialState,
  );

  useEffect(() => {
    if (!defaultValues?.length) return;

    setLoadingInterventions(true);

    getCitiesQuery({
      variables: {
        where: {
          id: {
            _in: defaultValues?.map((city) => city.id),
          },
        },
      },
    }).then(({ data }) => {
      setInterventions({
        type: 'insert',
        intervention:
          data?.city.map((intervention) => ({
            type: 'Feature',
            geometry: intervention.area as Polygon,
            properties: {
              ...intervention,
              type: defaultValues?.find((city) => city.id === intervention.id)
                ?.type,
            },
          })) ?? [],
      });
      setLoadingInterventions(false);
    });
  }, []);

  useEffect(() => {
    if (!interventions.features.length) return;

    setFieldValue(
      name,
      interventions.features
        .filter((intervention) =>
          ['include', 'exclude'].includes(intervention.properties?.type),
        )
        .map((intervention): ProFormUpdateFields['cities'][0] => ({
          id: Number(intervention.properties!.id),
          type: intervention.properties!.type,
          parentId: intervention.properties!.parentId,
        })),
    );
  }, [interventions]);

  const onPolygonDrawed = useCallback(
    (e: { shape: string; layer: Layer }) => {
      setLoadingInterventions(true);
      const { layer, shape } = e;

      layer.remove();

      if (shape === 'clear') {
        setLoadingInterventions(false);
        setInterventions({
          type: 'delete',
          intervention: interventions.features.filter((city) => {
            const gneu = booleanIntersects(
              (layer as L.Polygon).toGeoJSON().geometry,
              city.geometry,
            );
            return gneu;
          }),
        });
        return;
      }

      const geometry =
        layer instanceof L.Circle
          ? (toGeometryGeoJSON(
              layer.getLatLng(),
              layer.getRadius() / 1000,
            ) as Polygon)
          : (layer as L.Polygon).toGeoJSON().geometry;

      if (area(geometry) / 1000000 > 20000) {
        // La
        ToastError(
          'Attention',
          'La zone tracée ne peut pas dépasser les 20000km2 pour des soucis de performance',
        );
        setLoadingInterventions(false);
        return;
      }

      if (!geometry) {
        setLoadingInterventions(false);
        return;
      }

      getCitiesQuery({
        variables: {
          where: {
            area: {
              _stIntersects: geometry,
            },
          },
        },
      }).then(({ data }) => {
        setInterventions({
          type: 'upsert',
          intervention:
            data?.city.map((intervention) => ({
              type: 'Feature',
              geometry: intervention.area as Polygon,
              properties: {
                ...intervention,
                type: shape === 'exclude' ? 'exclude' : 'include',
              },
            })) ?? [],
        });

        setSelectionMode('click');
        setLoadingInterventions(false);
      });
    },
    [interventions, selectionMode],
  );

  const onDeleteFeatures = useCallback(async () => {
    setInterventions({
      type: 'delete',
      intervention: interventions.features,
    });

    // The department selection is kept, the cities one needs cities
    if (selectionMode === 'click') setSelectionMode('lasso');
  }, [selectionMode, interventions]);

  const onEachCityInterventionFeature = useCallback(
    (feature: FeatureIntervention, layer: Layer) => {
      layer.on({
        click: () => {
          const types = [...interventionTypes, 'touches'];

          const cityUpdated = {
            ...feature,
            properties: {
              ...feature.properties,
              // change the type of the city to the next one in the order
              type: types[
                (types.indexOf(feature.properties.type) + 1) % types.length
              ],
            },
          };

          setInterventions({
            type: 'update',
            intervention: [cityUpdated],
          });
        },
      });
    },
    [],
  );

  const onClickMap = useCallback(
    (e: LeafletMouseEvent) => {
      if (selectionMode === 'click') {
        getCitiesQuery({
          variables: {
            where: {
              area: {
                _stIntersects: toGeometryGeoJSON(e.latlng, 0.1),
              },
            },
          },
        }).then(({ data }) => {
          if (
            interventions.features.find(
              (city) => city.properties?.id === data?.city[0]?.id,
            )
          ) {
            return;
          }

          setInterventions({
            type: 'insert',
            intervention:
              data?.city.map((intervention) => ({
                type: 'Feature',
                geometry: intervention.area as Polygon,
                properties: {
                  ...intervention,
                  type: 'include',
                },
              })) ?? [],
          });
        });
      }
    },
    [selectionMode, interventions],
  );

  // Loaded only once the department selection is used, then kept in cache
  // (cache-first instead of the default cache-and-network: the departments
  // don't change)
  const { data: departmentsData } = useGetInterventionsFieldDepartmentsQuery({
    skip: selectionMode !== 'department',
    fetchPolicy: 'cache-first',
  });

  const departments = useMemo((): FeatureCollection<
    Geometry,
    DepartmentProperties
  > => {
    // Number of included or excluded cities by department
    const counts = new Map<number, number>();
    interventions.features.forEach((city) => {
      if (!['include', 'exclude'].includes(city.properties?.type)) return;
      const departmentId = city.properties?.departmentId;
      counts.set(departmentId, (counts.get(departmentId) ?? 0) + 1);
    });

    return {
      type: 'FeatureCollection',
      features:
        departmentsData?.department.map((department) => {
          const total = department.citiesAggregate.aggregate?.count ?? 0;
          const selected = counts.get(department.id) ?? 0;

          return {
            type: 'Feature',
            geometry: department.area,
            properties: {
              id: department.id,
              name: department.name,
              code: department.code,
              total,
              selected,
              status:
                selected === 0
                  ? 'none'
                  : selected >= total
                    ? 'selected'
                    : 'partial',
            },
          };
        }) ?? [],
    };
  }, [departmentsData, interventions]);

  // The department click handler is bound once, keep the latest value
  const loadingInterventionsRef = useRef(loadingInterventions);
  loadingInterventionsRef.current = loadingInterventions;

  const onEachDepartmentFeature = useCallback(
    (feature: FeatureDepartment, layer: Layer) => {
      layer.on({
        click: () => {
          // Ignore the clicks while the cities of a department are loading
          if (loadingInterventionsRef.current) return;

          const { id, status } = feature.properties;

          if (status === 'selected') {
            setInterventions({ type: 'unselectDepartment', departmentId: id });
            return;
          }

          setLoadingInterventions(true);

          getCitiesQuery({
            variables: {
              where: {
                departmentId: { _eq: id },
              },
            },
          }).then(({ data }) => {
            setInterventions({
              type: 'selectDepartment',
              intervention:
                data?.city.map((intervention) => ({
                  type: 'Feature',
                  geometry: intervention.area as Polygon,
                  properties: {
                    ...intervention,
                    type: 'include',
                  },
                })) ?? [],
            });
            setLoadingInterventions(false);
          });
        },
      });
    },
    [],
  );

  return (
    <InterventionsFieldView
      departments={departments}
      onEachDepartmentFeature={onEachDepartmentFeature}
      onChangeSelectionMode={setSelectionMode}
      canDraw={!interventions.features.length && !loadingInterventions}
      loadingInterventions={loadingInterventions}
      selectionMode={selectionMode}
      onDeleteFeatures={onDeleteFeatures}
      otherFeatures={otherFeatures}
      displayBilikZones={displayBilikZones}
      onPolygonDrawed={onPolygonDrawed}
      onClickMap={onClickMap}
      interventions={interventions}
      defaultMapCenter={defaultMapCenter}
      proPostalAddress={proPostalAddress}
      onEachCityInterventionFeature={onEachCityInterventionFeature}
      label={label}
    />
  );
};

export default InterventionsFieldContainer;
